import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { updateOverhead } from '../api';
import { UpdateProjectOverheadRequest } from '../types/dto/request/create-project-overhead.request';
import { invalidateOverheadsQueries } from './use-overheads-invalidation';

export const useUpdateOverhead = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.overheads');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: ({
      overheadId,
      data,
    }: {
      overheadId: number;
      data: UpdateProjectOverheadRequest;
    }) => updateOverhead(overheadId, data),
    onSuccess: async () => {
      // Close the modal first, then toast — a toast popping over a still-open
      // modal looks broken. Invalidation last: it awaits the refetch and would
      // delay both.
      onSuccess?.();
      displayToastSuccess(t('toasts.updateSuccess'));
      await invalidateOverheadsQueries(queryClient);
    },
    onError: async error => {
      displayToastErrorFromApiWithFallback(error, 'authenticated.overheads:toasts.updateError');
      // The overhead may have changed state meanwhile (e.g. became invoiced) —
      // refresh the list so the UI reflects the server state.
      await invalidateOverheadsQueries(queryClient);
    },
  });
};
